import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  type Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import clsx from "clsx";
import { createContext, use, useState } from "react";
import { getClosestLayer } from "@/utils/dom";
import styles from "./Dropdown.module.css";

interface DropdownProps {
  className?: string;
  placement?: Placement;
  padding?: number;
  /**
   * number - set fixed menu width
   * auto - width is set by the content, same as if not set
   * fit - set fixed width same as toggle width
   * fill - set min width same as toggle width and allow to expand
   */
  width?: number | "auto" | "fit" | "fill";

  disabled?: boolean;
  loading?: boolean;

  onClose?: () => void;
}

export const Dropdown: React.FC<React.PropsWithChildren<DropdownProps>> & {
  Context: typeof DropdownContext;
  Toggle: typeof Toggle;
  Menu: typeof Menu;
} = ({
  className,
  placement = "bottom",
  padding = 12,
  width = "fill",
  children,
  disabled = false,
  loading = false,
  onClose,
}) => {
  const [open, setOpen] = useState(false);

  const floating = useFloating({
    open,
    onOpenChange: (open) => {
      setOpen(open);
      onClose?.();
    },
    strategy: "fixed",
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(padding),
      flip(),
      shift({
        mainAxis: true,
        crossAxis: true,
        padding,
      }),
      size({
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty(
            "max-height",
            `${availableHeight - padding}px`,
          );
        },
      }),
    ],
  });

  const click = useClick(floating.context);

  const dismiss = useDismiss(floating.context);

  const role = useRole(floating.context, { role: "listbox" });

  const interactions = useInteractions([click, dismiss, role]);

  const context = {
    open,
    close: () => {
      setOpen(false);
      onClose?.();
    },
    width,
  };

  const innerContext = {
    floating,
    interactions,
    onClose,
  };

  return (
    <DropdownContext.Provider value={context}>
      <DropdownInnerContext.Provider value={innerContext}>
        <div
          className={clsx(
            styles.dropdown,
            className,
            disabled && styles.disabled,
            loading && styles.loading,
          )}
        >
          {children}
        </div>
      </DropdownInnerContext.Provider>
    </DropdownContext.Provider>
  );
};

const DropdownContext = createContext<{
  open: boolean;
  close: () => void;
  width: DropdownProps["width"];
}>({
  open: false,
  close: () => {},
  width: "fit",
});

const DropdownInnerContext = createContext<{
  floating: ReturnType<typeof useFloating>;
  interactions: ReturnType<typeof useInteractions>;
  onClose?: () => void;
}>({
  floating: undefined as unknown as ReturnType<typeof useFloating>,
  interactions: undefined as unknown as ReturnType<typeof useInteractions>,
});

const Toggle: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  const { floating, interactions } = use(DropdownInnerContext);

  return (
    <div
      ref={floating.refs.setReference}
      className={clsx(styles.toggle, className)}
      {...interactions.getReferenceProps({
        onClick(event) {
          event.preventDefault();
          event.stopPropagation();
        },
      })}
    >
      {children}
    </div>
  );
};

const Menu: React.FC<
  React.PropsWithChildren<{
    className?: string;
  }>
> = ({ className, children }) => {
  const context = use(DropdownContext);

  if (context.open === false) {
    return null;
  }

  const { floating, interactions } = use(DropdownInnerContext);

  const minWidth = getMinWidth(context.width, floating);
  const maxWidth = getMaxWidth(context.width, floating);

  return (
    <FloatingPortal root={getClosestLayer(floating.elements.domReference)}>
      <div
        ref={floating.refs.setFloating}
        className={clsx(styles.menu, className)}
        style={{
          ...floating.floatingStyles,
          minWidth,
          maxWidth,
        }}
        {...interactions.getFloatingProps({
          onClick(event) {
            event.stopPropagation();
          },
        })}
      >
        {children}
      </div>
    </FloatingPortal>
  );
};

const getMinWidth = (
  width: DropdownProps["width"],
  floating: ReturnType<typeof useFloating>,
) => {
  if (typeof width === "number" || width === "auto") {
    return width;
  }

  const referenceWidth = floating.elements.domReference?.clientWidth ?? 0;
  const floatingWidth = floating.elements.floating?.clientWidth ?? 0;

  if (width === "fit") {
    return referenceWidth;
  }

  if (width === "fill") {
    return referenceWidth >= floatingWidth ? referenceWidth : "auto";
  }

  return undefined;
};

const getMaxWidth = (
  width: DropdownProps["width"],
  floating: ReturnType<typeof useFloating>,
) => {
  const referenceWidth = floating.elements.domReference?.clientWidth ?? 0;

  if (width === "fit") {
    return referenceWidth;
  }

  if (width === "fill") {
    return `calc(100% - 12px * 2)`;
  }

  return undefined;
};

Dropdown.Context = DropdownContext;
Dropdown.Toggle = Toggle;
Dropdown.Menu = Menu;
